import { CURRENCY_META, fmt, rateOf } from '@k2pos/core';
import type { ReactElement } from 'react';
import { useNavigate } from 'react-router-dom';
import { K2Logo } from '@/components/brand/K2Logo';
import { AppBar } from '@/components/ui/AppBar';
import { Card } from '@/components/ui/Card';
import { Mono, Text } from '@/components/ui/Text';
import { IconChevronRight, IconGrid, IconList, IconPlus, IconRows, IconTruck, IconWallet, IconChart } from '@/components/icons';
import { usePos } from '@/store/pos';
import { colors } from '@/theme/tokens';
import styles from './More.module.css';

/** Everything that is not the daily sell/collect loop. */
export function More() {
  const navigate = useNavigate();
  const catalog = usePos((s) => s.catalog);
  const shop = usePos((s) => s.shop);
  const lowCount = catalog.filter(
    (p) => p.remaining !== undefined && p.lowStockAt !== undefined && p.remaining <= p.lowStockAt,
  ).length;

  return (
    <>
      <AppBar title="ອື່ນໆ" />

      <div className={`${styles.body} vscroll`}>
        <MenuRow
          icon={<IconGrid size={16} />}
          label="ສະຕັອກ"
          sub={
            lowCount > 0
              ? `${catalog.length} ລາຍການ · ${lowCount} ເຫຼືອໜ້ອຍ`
              : `${catalog.length} ລາຍການ`
          }
          onPress={() => navigate('/stock')}
        />
        <MenuRow
          icon={<IconTruck size={16} />}
          label="ຮັບເຄື່ອງເຂົ້າຮ້ານ"
          sub="ບັນທຶກການຊື້ · ເພີ່ມສະຕັອກ ແລະ ຕົ້ນທຶນ"
          onPress={() => navigate('/purchase')}
        />
        <MenuRow
          icon={<IconTruck size={16} />}
          label="ປະຫວັດຮັບເຄື່ອງ"
          sub="ຊື້ຫຍັງ · ຈາກໃຜ · ຈ່າຍແລ້ວ ຫຼື ຄ້າງ"
          onPress={() => navigate('/purchases')}
        />
        <MenuRow
          icon={<IconList size={16} />}
          label="ອໍເດີ້"
          sub="ບິນທັງໝົດ · ເປີດເບິ່ງ ແລະ ພິມຄືນໄດ້"
          onPress={() => navigate('/orders')}
        />
        <MenuRow
          icon={<IconWallet size={16} />}
          label="ລົງລາຍຈ່າຍ"
          sub="ຄ່າຂົນສົ່ງ · ຄ່າໄຟ · ຄ່າແຮງງານ"
          onPress={() => navigate('/expense')}
        />
        <MenuRow
          icon={<IconChart size={16} />}
          label="ກຳໄລ ຂາດທຶນ"
          sub="ສະຫຼຸບຕາມຊ່ວງ · ກຳໄລ ແລະ ເງິນສົດ"
          onPress={() => navigate('/pl')}
        />
        <MenuRow
          icon={<IconRows size={16} />}
          label="ປິດຍອດ · ລາຍງານ"
          sub="ສະຫຼຸບຂາຍ ແລະ ນັບເງິນທ້າຍມື້"
          onPress={() => navigate('/report')}
        />
        <MenuRow
          icon={<IconPlus size={16} />}
          label="ເພີ່ມສິນຄ້າ"
          sub="ແມ່ແບບ ຫຼື ພິມເອງ"
          onPress={() => navigate('/add-product')}
        />

        <Text weight={800} size={11} color={colors.textMuted} className={styles.label}>
          ຮ້ານ
        </Text>
        {/* One row, not two. There was a ຕັ້ງຄ່າຮ້ານ menu row and a shop card
            directly under it, both going to /settings — the same destination
            twice, which reads as two different places until you tap them. The
            card wins because it names the shop you are signed in to, which the
            generic label could not; its sub-line takes over what settings
            actually holds. */}
        <button type="button" onClick={() => navigate('/settings')} className={styles.menuBtn}>
          <Card className={styles.shop}>
            <K2Logo size={40} />
            <div className="grow col">
              <Text weight={800} size={14} lines={1} align="left">
                {shop.name}
              </Text>
              <Text size={11} color={colors.textMuted} lines={1} align="left">
                ຕັ້ງຄ່າຮ້ານ · ໃບບິນ · ອັດຕາແລກປ່ຽນ · ໜ່ວຍ ແລະ ໝວດ
              </Text>
            </div>
            <IconChevronRight size={17} className={styles.chevron} />
          </Card>
        </button>

        <Text weight={800} size={11} color={colors.textMuted} className={styles.label}>
          ອັດຕາແລກປ່ຽນ
        </Text>
        {CURRENCY_META.filter((c) => c.code !== 'LAK').map((c) => (
          <Card key={c.code} className={styles.rate}>
            <div className="grow col">
              <Text weight={700} size={13}>
                {c.sym} {c.label}
              </Text>
              <Text size={10.5} color={colors.textMuted}>
                ເກັບເປັນກີບສະເໝີ
              </Text>
            </div>
            <Mono weight={700} size={13}>
              1 {c.sym} = {fmt(rateOf(c.code, shop.rates))} ₭
            </Mono>
          </Card>
        ))}

        <Mono size={10} color={colors.textFaint} align="center" className={styles.brand}>
          K2TECH
        </Mono>
      </div>
    </>
  );
}

function MenuRow({
  icon,
  label,
  sub,
  onPress,
}: {
  icon: ReactElement;
  label: string;
  sub: string;
  onPress: () => void;
}) {
  return (
    <button type="button" onClick={onPress} className={styles.menuBtn}>
      <Card className={styles.menuRow}>
        <span className={styles.icon}>{icon}</span>
        <div className="grow col">
          <Text weight={700} size={14} align="left" lines={1}>
            {label}
          </Text>
          <Text size={11} color={colors.textMuted} align="left" lines={1}>
            {sub}
          </Text>
        </div>
        <IconChevronRight size={17} className={styles.chevron} />
      </Card>
    </button>
  );
}
